'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  Bell,
  CheckCircle2,
  AlertCircle,
  Smartphone,
  MessageSquare,
  CalendarCheck,
  CalendarX,
  RefreshCw,
  CreditCard,
  UserPlus,
  ArrowRight,
  ShieldCheck
} from 'lucide-react';
import { SettingsSidebar } from '@/components/dashboard/settings/SettingsSidebar';

interface PushPreferences {
  newBookings: boolean;
  cancellations: boolean;
  reschedules: boolean;
  mercadopagoPayments: boolean;
  failedReminders: boolean;
  newClients: boolean;
}

const DEFAULT_PREFS: PushPreferences = {
  newBookings: true,
  cancellations: true,
  reschedules: true,
  mercadopagoPayments: true,
  failedReminders: true,
  newClients: true,
};

export default function RemindersSettingsPage() {
  const [prefs, setPrefs] = useState<PushPreferences>(DEFAULT_PREFS);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [savedSuccess, setSavedSuccess] = useState<boolean>(false);

  useEffect(() => {
    // Cargar preferencias locales
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('appgenda_push_preferences');
        if (stored) {
          setPrefs(JSON.parse(stored));
        }
      } catch (e) {
        console.warn('Error reading push prefs from localStorage:', e);
      }

      if ('Notification' in window) {
        setNotificationPermission(Notification.permission);
      } else {
        setNotificationPermission('unsupported');
      }
    }
  }, []);

  const handleToggle = (key: keyof PushPreferences) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    if (typeof window !== 'undefined') {
      localStorage.setItem('appgenda_push_preferences', JSON.stringify(updated));
    }
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };

  const requestNotificationPermission = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        const perm = await Notification.requestPermission();
        setNotificationPermission(perm);
        if (perm === 'granted') {
          new Notification('AppGenda Activo', {
            body: '¡Notificaciones habilitadas! Recibirás alertas inmediatas de tus reservas.',
            icon: '/favicon.ico'
          });
        }
      } catch (err) {
        console.error('Error requesting notification permission:', err);
      }
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Cabecera */}
      <div className="space-y-1">
        <Link
          href="/dashboard/settings"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Volver a Configuración</span>
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <Bell className="w-6 h-6 text-emerald-600" />
              Avisos y recordatorios
            </h1>
            <p className="text-sm text-slate-500">
              Elegí qué avisos querés recibir en este dispositivo cuando pasen cosas en tu negocio.
            </p>
          </div>

          {savedSuccess && (
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold animate-in fade-in">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Guardado en este navegador</span>
            </div>
          )}
        </div>
      </div>

      {/* Grid con Sidebar Unificado */}
      <div className="grid grid-cols-12 gap-6 items-start">
        {/* Sidebar */}
        <div className="col-span-12 lg:col-span-3">
          <SettingsSidebar currentPath="/dashboard/settings/reminders" />
        </div>

        {/* Contenido Principal */}
        <div className="col-span-12 lg:col-span-9 space-y-6">
          {/* Advertencia Local/Dispositivo */}
          <div className="bg-amber-50/80 border border-amber-200/90 rounded-3xl p-5 flex items-start gap-3.5 text-amber-900">
            <Smartphone className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs space-y-1 leading-relaxed">
              <p className="font-bold text-amber-950">Avisos por dispositivo</p>
              <p>
                Estas preferencias son de este dispositivo: si usás AppGenda en otra computadora o teléfono, configuralas ahí también.
              </p>
            </div>
          </div>

          {/* Estado de Permisos de Navegador */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Bell className="w-4 h-4 text-slate-600" />
                Notificaciones push del navegador
              </h2>
              <p className="text-xs text-slate-500">
                {notificationPermission === 'granted'
                  ? 'Las alertas push están activas y autorizadas para este navegador.'
                  : notificationPermission === 'denied'
                  ? 'Las notificaciones están bloqueadas en tu navegador. Podés desbloquearlas desde el candado de la URL.'
                  : 'Autorizá al navegador para mostrarte alertas en tiempo real en tu escritorio o celular.'}
              </p>
            </div>

            <div>
              {notificationPermission === 'granted' ? (
                <span className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl bg-emerald-50 text-emerald-800 text-xs font-bold border border-emerald-200">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  Activadas
                </span>
              ) : notificationPermission === 'unsupported' ? (
                <span className="text-xs text-slate-400 font-medium">Navegador sin soporte push</span>
              ) : (
                <button
                  type="button"
                  onClick={requestNotificationPermission}
                  className="px-4 py-2 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold shadow-sm transition-all cursor-pointer"
                >
                  Activar notificaciones
                </button>
              )}
            </div>
          </div>

          {/* Bloque 1: Actividad de turnos */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-5">
            <div>
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider text-[11px] text-slate-400">
                Actividad de Turnos
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Alertas sobre la agenda y el flujo de clientes
              </p>
            </div>

            <div className="divide-y divide-slate-100">
              {/* Nuevas reservas */}
              <div className="py-3.5 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-xl bg-emerald-50 text-emerald-700 shrink-0 mt-0.5">
                    <CalendarCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900">Nuevas reservas</h3>
                    <p className="text-xs text-slate-500">Te avisamos apenas un cliente agenda desde tu página web.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('newBookings')}
                  className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                    prefs.newBookings ? 'bg-emerald-800' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                      prefs.newBookings ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Cancelaciones */}
              <div className="py-3.5 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-xl bg-rose-50 text-rose-700 shrink-0 mt-0.5">
                    <CalendarX className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900">Cancelaciones</h3>
                    <p className="text-xs text-slate-500">Te avisamos si un cliente cancela su turno.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('cancellations')}
                  className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                    prefs.cancellations ? 'bg-emerald-800' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                      prefs.cancellations ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Reprogramaciones */}
              <div className="py-3.5 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-xl bg-amber-50 text-amber-700 shrink-0 mt-0.5">
                    <RefreshCw className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900">Reprogramaciones</h3>
                    <p className="text-xs text-slate-500">Te avisamos si un cliente cambia la fecha u hora de su turno.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('reschedules')}
                  className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                    prefs.reschedules ? 'bg-emerald-800' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                      prefs.reschedules ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Bloque 2: Cobros y alertas operativas */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-5">
            <div>
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider text-[11px] text-slate-400">
                Cobros y Operaciones
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Transacciones financieras y novedades del CRM
              </p>
            </div>

            <div className="divide-y divide-slate-100">
              {/* Cobros de MP */}
              <div className="py-3.5 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-xl bg-blue-50 text-blue-700 shrink-0 mt-0.5">
                    <CreditCard className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900">Cobros de Mercado Pago</h3>
                    <p className="text-xs text-slate-500">Te avisamos cuando se acredita el pago de una seña o turno completo.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('mercadopagoPayments')}
                  className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                    prefs.mercadopagoPayments ? 'bg-emerald-800' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                      prefs.mercadopagoPayments ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Recordatorios fallidos */}
              <div className="py-3.5 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-xl bg-rose-50 text-rose-700 shrink-0 mt-0.5">
                    <AlertCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900">Recordatorios fallidos</h3>
                    <p className="text-xs text-slate-500">Te avisamos si algún WhatsApp no se pudo entregar (por ejemplo si el número no existe o WhatsApp se desconectó).</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('failedReminders')}
                  className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                    prefs.failedReminders ? 'bg-emerald-800' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                      prefs.failedReminders ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Clientes nuevos */}
              <div className="py-3.5 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-xl bg-purple-50 text-purple-700 shrink-0 mt-0.5">
                    <UserPlus className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900">Clientes nuevos</h3>
                    <p className="text-xs text-slate-500">Te avisamos cuando la persona que reserva es la primera vez que viene.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('newClients')}
                  className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                    prefs.newClients ? 'bg-emerald-800' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                      prefs.newClients ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Bloque 3: Acceso Directo a WhatsApp a tus Clientes */}
          <div className="bg-emerald-950 text-white rounded-3xl p-6 sm:p-7 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div className="space-y-1.5 max-w-xl">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-900/60 px-2.5 py-1 rounded-full inline-block">
                Mensajería Saliente
              </span>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-emerald-400" />
                Mensajes automáticos por WhatsApp
              </h2>
              <p className="text-xs text-emerald-200/90 leading-relaxed">
                Configurá los recordatorios que le llegan a tus clientes antes de su turno para evitar ausencias y huecos en la agenda. Salen desde tu propio número conectado.
              </p>
            </div>

            <Link
              href="/dashboard/whatsapp"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold transition-all shrink-0 shadow-sm"
            >
              <span>Configurar WhatsApp</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
